import { useSnackbar } from "../../../components/minimal/snackbar";
import { useLocales } from "../../../locales";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { Alert, Box, Grid, Stack, MenuItem } from "@mui/material";
import FormProvider, { RHFAutocomplete, RHFTextField, RHFSelect } from "../../../components/minimal/hook-form";
import { LoadingButton } from "@mui/lab";
import * as API from "../../../api";
import RHFSwitch from "../../../components/minimal/hook-form/RHFSwitch";
import { useParams } from "react-router-dom";
import { useDashboardContext } from "../../../context-state/DashboardContext";
import { usePermissionContext } from "../../../context-state/PermissionContext";
import { useAuthContext } from "../../../context-state/AuthContext";
import { Typography } from "@mui/material";
import { isBuyerHiddenTicket } from '../../../consts/index.js';
import { getMinTypes, isRegularTicket, linkExposesTooFewTypes } from '../../../utils/ticketTypeSelection';

export const LinkForm = ({ link, onSuccess, buttonText }) => {

    const { enqueueSnackbar } = useSnackbar();
    const { translate } = useLocales()
    const { subId, eventId } = useParams()
    const { ticketContext, linkContext, eventContext } = useDashboardContext();
    const { getTicketsOfEvent, getTicket } = ticketContext
    const { updateManyLinks, updateEventToLinksRelation, updateLink } = linkContext
    const { account } = useAuthContext()
    const { collaboratesContext } = usePermissionContext();
    const { collaboratesByAccount } = collaboratesContext

    const isUpdateMode = !!link?._id
    const currentTickets = []

    // Get collaborators for this account
    const collaborates = collaboratesByAccount[account._id] || []
    const collaboratorOptions = collaborates.map(collaborate => ({
        value: collaborate.user._id,
        label: `${collaborate.user.fname} ${collaborate.user.lname || ''}`.trim()
    }))

    // One derived list, reused for the picker, the default selection and the name→id lookup on save.
    // It drops two things: map misses (getTicket is a raw lookup, and dereferencing a miss used to
    // crash here) and the group-capacity ticket, which must never be attachable to a public link —
    // it is ₪0 and a link exposes a hidden ticket to buyers. Free tickets deliberately STAY: putting
    // one on a link is how they are distributed, and production has live links doing exactly that.
    const selectableTickets = getTicketsOfEvent(eventId)
        .map((id) => getTicket(id))
        .filter((ticket) => ticket && !isBuyerHiddenTicket(ticket))

    selectableTickets.forEach((ticket) => {
        if (link?.tickets?.length && link.tickets.includes(ticket._id)) currentTickets.push(ticket.name)
    })
    const tickets = selectableTickets.map((ticket) => ticket.name)

    const TicketsSchema = Yup.object().shape({
        name: Yup.string().required(translate('dashboard.events.required_field')),
        tickets: Yup.array().min(1, translate('dashboard.events.required_field')),
        countTicketsAllowPerPurchase: Yup.number().min(1, translate('dashboard.events.required_field')),
        collaborator: Yup.string().optional(),
    });

    const defaultValues = {
        name: link ? link.name : '',
        is_enabled: link ? link.is_enabled : true,
        // Only a NEW link defaults to everything. An existing link keeps its own selection, even
        // when that resolves to nothing selectable — otherwise editing it would silently attach all.
        tickets: link ? currentTickets : tickets,
        description: link ? link.description : '',
        countTicketsAllowPerPurchase: (link && link.countTicketsAllowPerPurchase) ? link.countTicketsAllowPerPurchase : 1,
        collaborator: link ? link.collaborator || '' : '',
    }

    if (!isUpdateMode) {
        defaultValues.amountToCreate = 1
    }


    const methods = useForm({ resolver: yupResolver(TicketsSchema), defaultValues });

    const { handleSubmit, watch, formState: { isSubmitting } } = methods;

    // #1556 §6.4: the link's ticket subset is applied FE-side only, so an order-scope minimum the
    // link cannot reach makes it unusable for buyers. Non-blocking (a free-ticket-only link is
    // legitimate: orders with zero regular tickets are never limited). Group scope has no minimum.
    const event = eventContext?.getEvent?.(eventId)
    const selectedTicketNames = watch('tickets') || []
    const selectedLinkTickets = selectableTickets.filter((ticket) => selectedTicketNames.includes(ticket.name))
    const showBelowMinWarning = linkExposesTooFewTypes(event, selectedLinkTickets)
    const linkMinTypes = showBelowMinWarning ? getMinTypes(event) : null
    const selectedRegularCount = showBelowMinWarning ? selectedLinkTickets.filter(isRegularTicket).length : 0

    const onSubmit = async (data) => {
        try {
            // map from name to id because the AutoSelect component make problems...
            // Resolved against selectableTickets so a name with no match is skipped instead of
            // throwing, and so a system ticket can never be attached even if one reached the field.
            const ticketsIds = data.tickets
                .map((ticketName) => selectableTickets.find((ticket) => ticket.name === ticketName))
                .filter(Boolean)
                .map((ticket) => ticket._id)


            if (isUpdateMode) {
                link.name = data.name
                link.description = data.description
                link.is_enabled = data.is_enabled
                link.countTicketsAllowPerPurchase = data.countTicketsAllowPerPurchase ? data.countTicketsAllowPerPurchase : 1
                link.tickets = ticketsIds
                link.collaborator = data.collaborator || null

                const updatedLinkResponse = await API.updateLinkOfEvent({ accountId: subId, eventId, linkId: link._id, linkData: link })
                updateLink(updatedLinkResponse.data)
                /* On Success update */
                enqueueSnackbar(translate('snackbar.link_updated_successfully'))
                onSuccess()
                return
            }

            // Create many links
            const amountToCreate = data.amountToCreate ? Number(data.amountToCreate) : 1
            const linksToCreate = []
            for (let i = 0; i < amountToCreate; i++) {
                linksToCreate.push({
                    name: `${data.name} #${i + 1}`,
                    description: data.description,
                    is_enabled: data.is_enabled,
                    countTicketsAllowPerPurchase: data.countTicketsAllowPerPurchase ? data.countTicketsAllowPerPurchase : 1,
                    tickets: ticketsIds,
                    collaborator: data.collaborator || null,
                })
            }

            const createLinksResponse = await API.createManyLinksOfEvent({ accountId: subId, eventId, linksDataArr: linksToCreate })
            updateManyLinks(createLinksResponse.data)
            updateEventToLinksRelation(eventId, createLinksResponse.data)
            /* On Success create */
            enqueueSnackbar(translate('snackbar.link_created_successfully'));
            onSuccess()

        } catch (error) {
            enqueueSnackbar(translate('general_error'), { variant: 'error' })
            console.error(error);
        }
    };

    return (
        <FormProvider width="100%" methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                    <Box
                        columnGap={2}
                        rowGap={1}
                        display="grid"
                        gridTemplateColumns={{ xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' }}
                    >
                        <RHFTextField name="name" label={translate('links.form.name')} />
                        <RHFTextField name="description" label={translate('links.form.description')} />
                        <RHFTextField
                            name="countTicketsAllowPerPurchase"
                            type="number"
                            label={translate('links.form.ticketsPerPurchase')}
                            helperText={translate('links.form.ticketsPerPurchaseHelper')}
                        />
                        <RHFSelect
                            name="collaborator"
                            label={translate('collaborator_name')}
                            helperText={!collaboratorOptions.length ? (
                                <span>
                                    {translate('links.form.noCollaboratorsHelper')} <a
                                        href={`/dashboard/sub/${subId}/collaborate`}
                                    >
                                        {translate('links.form.clickToInviteMarketers')}
                                    </a>
                                </span>
                            ) : undefined}
                        >
                            <MenuItem value="">
                                <em>{translate('links.form.noCollaborator')}</em>
                            </MenuItem>
                            {collaboratorOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </RHFSelect>
                        {
                            isUpdateMode ? null : <RHFTextField
                                name="amountToCreate"
                                type="number"
                                label={translate('links.form.amountToCreate')}
                            />
                        }
                        <RHFSwitch
                            sx={{ alignItems: 'center' }}
                            name="is_enabled"
                            label={translate('links.form.isActive')}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <RHFAutocomplete
                            name="tickets"
                            label={translate('links.form.tickets')}
                            multiple
                            freeSolo={false}
                            options={tickets}
                            helperText={translate('links.form.ticketsHelper')}
                            ChipProps={{ size: 'medium' }}
                        />
                        {showBelowMinWarning ? (
                            <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="link-selection-limit-warning">
                                {translate('ticketSelectionLimit.link.warn_below_min', { n: selectedRegularCount, min: linkMinTypes })}
                            </Alert>
                        ) : null}
                    </Box>

                    <Stack alignItems="flex-end" sx={{ my: 3 }}>
                        <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                            {buttonText}
                        </LoadingButton>
                    </Stack>
                </Grid>
            </Grid>
        </FormProvider>
    )
}
